// Modals: DetailModal (full-bleed sheet), ShareSheet (3 modes), RateSheet
// (multi-axis + stars + note), LocationPicker, PlanDetailSheet.

import React from 'react';
import { HeroArt, Stars, Icon, Constellation, GenerativeAvatar, FriendAvatar } from './components.jsx';
import { matchScore } from './lib/match.js';
import { distLabel } from './lib/geo.js';
import { googleCalendarUrl, outlookCalendarUrl, downloadIcs } from './lib/calendar.js';
import { useHomeBase } from './context.js';
import { trackEvent, placeKey } from './lib/analytics.js';
import { LOCATIONS } from './data.js';
import {
  larkleGetUid,
  apiFetchPlaceReviews, apiUpdatePlan, apiInviteToPlan, apiRemoveFromPlan,
  apiFinalizePlan, apiCancelPlan, apiLeavePlan, apiRsvpToPlan,
  apiFetchLocation, placeToActivity,
} from './lib/api.js';

// Shared: escape-to-close + focus the dialog when a sheet mounts.
function useSheetA11y(onClose) {
  const ref = React.useRef(null);
  const closeRef = React.useRef(onClose);
  closeRef.current = onClose;
  React.useEffect(() => {
    // preventScroll: the sheet is still translated offscreen when focus lands
    // (mid slide-in animation) — a plain focus() scroll-into-views it and
    // yanks the whole app container up and down. Mount-only: re-focusing on
    // every parent re-render restarts that jolt while the sheet is animating.
    ref.current?.focus?.({ preventScroll: true });
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return ref;
}

export function shareUrlFor(activityId) {
  return `${window.location.origin}/a/${activityId}`;
}

// ── Detail ────────────────────────────────────────────────────────────────
export function DetailModal({ activity, vibe, isSaved, doneEntry, onClose, onSave, onShare, onPlanIt, onMarkDone }) {
  const base = useHomeBase();
  const sheetRef = useSheetA11y(onClose);
  const [reviewText, setReviewText] = React.useState(null);
  const [reviewLoading, setReviewLoading] = React.useState(true);

  React.useEffect(() => {
    setReviewText(null);
    if (!activity?._place) { setReviewLoading(false); return; }
    // blurb is place.description with the place name as fallback — when it's a
    // real description, show it and skip fetching a review to stand in for one.
    if (activity.blurb && activity.blurb.trim() !== activity.name.trim()) {
      setReviewLoading(false);
      return;
    }
    setReviewLoading(true);
    apiFetchPlaceReviews(activity.id)
      .then(reviews => {
        const first = Array.isArray(reviews) ? reviews.find(r => r.reviewText) : null;
        setReviewText(first?.reviewText || null);
      })
      .catch(() => {})
      .finally(() => setReviewLoading(false));
  }, [activity?.id]);

  if (!activity) return null;

  const directionsUrl = activity.lat != null
    ? `https://www.google.com/maps/dir/?api=1&destination=${activity.lat},${activity.lon}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activity.name + ' ' + (activity.address || ''))}`;

  return (
    <>
      <div className="scrim" onClick={onClose}/>
      <div ref={sheetRef} tabIndex={-1} role="dialog" aria-label={activity.name} className="detail-sheet">
        <div style={{ position: 'relative', height: 320, flexShrink: 0 }}>
          <HeroArt activity={activity} height="100%" rounded={0} swipePhotos/>
          {/* pointerEvents:none so the empty space around these buttons doesn't
              block carousel swipes — only the buttons themselves opt back in */}
          <div style={{
            position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 16px)', left: 16, right: 16,
            display: 'flex', justifyContent: 'space-between', pointerEvents: 'none',
          }}>
            <button className="iconbtn" style={{
              background: 'rgba(0,0,0,0.4)', color: 'var(--paper)',
              backdropFilter: 'blur(8px)', borderColor: 'transparent', pointerEvents: 'auto',
            }} onClick={onClose} aria-label="Close">
              <span style={{ width: 18, height: 18 }}>{Icon.x}</span>
            </button>
            <div className="row" style={{ gap: 8 }}>
              <button className="iconbtn" style={{
                background: 'rgba(0,0,0,0.4)', color: 'var(--paper)',
                backdropFilter: 'blur(8px)', borderColor: 'transparent', pointerEvents: 'auto',
              }} onClick={() => onShare(activity.id)} aria-label="Share">
                <span style={{ width: 18, height: 18 }}>{Icon.share}</span>
              </button>
              <button className="iconbtn" style={{
                background: isSaved ? 'var(--coral)' : 'rgba(0,0,0,0.4)',
                color: 'var(--paper)',
                backdropFilter: 'blur(8px)', borderColor: 'transparent', pointerEvents: 'auto',
              }} onClick={() => onSave(activity.id)} aria-label={isSaved ? 'Remove from saved' : 'Save'}>
                <span style={{ width: 18, height: 18 }}>{isSaved ? Icon.bookmarkFill : Icon.bookmark}</span>
              </button>
            </div>
          </div>
          {/* bottom title block — no interactive children, so the whole thing
              passes swipes through to the carousel underneath */}
          <div style={{
            position: 'absolute', left: 0, right: 0, bottom: 0,
            padding: '40px 22px 22px',
            background: 'linear-gradient(to top, rgba(20,10,8,0.95) 0%, rgba(20,10,8,0.5) 60%, transparent 100%)',
            color: 'var(--paper)', pointerEvents: 'none',
          }}>
            <div className="row" style={{ gap: 8, marginBottom: 8 }}>
              {activity.kind && <span className="chip dark" style={{ height: 22, fontSize: 10.5 }}>{activity.kind}</span>}
            </div>
            <h2 className="serif" style={{ fontSize: 32, lineHeight: 1.05, margin: 0, color: 'var(--paper)', fontWeight: 400, textWrap: 'pretty' }}>
              {activity.name}
            </h2>
          </div>
        </div>

        <div style={{
          flex: 1, background: 'var(--paper)', borderRadius: '24px 24px 0 0', marginTop: -20,
          padding: '32px 22px 22px', overflow: 'auto',
        }}>
          {/* stats row — distance, vibe match, and the match map together */}
          <div className="row between" style={{ gap: 12, marginBottom: 18 }}>
            <div className="row" style={{ gap: 16, flex: 1, minWidth: 0 }}>
              <Stat label="Distance" value={distLabel(activity, base)} sub="from your pin"/>
              {!isSaved && (
                <div className="col" style={{ gap: 1, flex: 1, minWidth: 0 }}>
                  <span className="upper" style={{ fontSize: 9.5, color: 'var(--ink-mute)', fontWeight: 600 }}>Vibe match</span>
                  <div className="row" style={{ gap: 6, alignItems: 'baseline' }}>
                    <span className="serif" style={{ fontSize: 32, color: 'var(--terracotta)', lineHeight: 1 }}>
                      {matchScore(vibe, activity)}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>/ 100</span>
                  </div>
                </div>
              )}
            </div>
            {!isSaved && <Constellation user={vibe} activity={activity} size={110}/>}
          </div>

          <div className="divider"/>

          {/* location description */}
          <div style={{ marginBottom: 18 }}>
            <div className="upper" style={{ fontSize: 9.5, color: 'var(--terracotta)', fontWeight: 700, marginBottom: 8 }}>
              description
            </div>
            {reviewLoading ? (
              <div className="col" style={{ gap: 8 }}>
                {['100%', '92%', '60%'].map((w, i) => (
                  <div key={i} style={{
                    height: 13, width: w, borderRadius: 6,
                    background: 'var(--bg-tint)',
                    animation: `larkle-shimmer 1.4s ease-in-out ${i * 0.1}s infinite`,
                  }}/>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-soft)', margin: 0, textWrap: 'pretty' }}>
                {reviewText || activity.blurb}
              </p>
            )}
          </div>

          {/* tags */}
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
            {[...new Set(activity.tags)].map((t, i) => <span key={`${t}-${i}`} className="chip">{t}</span>)}
          </div>

          {/* address */}
          <div className="row between" style={{ marginBottom: 18, paddingBottom: 14, borderBottom: '0.5px solid var(--hair)' }}>
            <div className="row" style={{ gap: 10 }}>
              <span className="iconbtn" style={{ width: 30, height: 30, color: 'var(--ink)' }}>{Icon.pin}</span>
              <div className="col">
                <span style={{ fontSize: 13, fontWeight: 500 }}>{activity.address}</span>
                <span className="muted" style={{ fontSize: 11.5 }}>{distLabel(activity, base)} away</span>
              </div>
            </div>
            <a className="chip coral" href={directionsUrl} target="_blank" rel="noreferrer"
               onClick={() => trackEvent('get_directions', { place_id: activity.id, place_key: placeKey(activity) })}
               style={{ cursor: 'pointer', height: 28, padding: '0 12px', textDecoration: 'none' }}>Directions →</a>
          </div>

          {/* Make it a plan */}
          {onPlanIt && (
            <div style={{
              padding: '14px 16px', borderRadius: 14, marginBottom: 18,
              background: 'var(--bg-tint)', border: '0.5px solid var(--hair)',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <span className="iconbtn solid" style={{ width: 38, height: 38, flexShrink: 0 }}>
                <span style={{ width: 17, height: 17 }}>{Icon.cal}</span>
              </span>
              <div className="col" style={{ flex: 1, gap: 1, minWidth: 0 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>Thinking of going?</span>
                <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                  Pick a time and rally the crew around this spot.
                </span>
              </div>
              <button className="btn coral" style={{ height: 36, padding: '0 15px', fontSize: 12.5, flexShrink: 0 }}
                      onClick={() => onPlanIt(activity.id)}>
                Make a plan
              </button>
            </div>
          )}

          {doneEntry && (
            <div style={{
              padding: '14px 16px', borderRadius: 14,
              background: 'oklch(0.96 0.04 30)', marginBottom: 18,
              border: '1px solid oklch(0.85 0.12 35)',
            }}>
              <div className="row between" style={{ marginBottom: 6 }}>
                <span className="upper" style={{ fontSize: 10, color: 'var(--terracotta)', fontWeight: 700 }}>
                  You went · {doneEntry.when}
                </span>
                <Stars value={doneEntry.stars} size={14}/>
              </div>
              {doneEntry.note && (
                <p className="serif" style={{ fontSize: 16, color: 'var(--ink)', margin: 0, lineHeight: 1.3 }}>
                  “{doneEntry.note}”
                </p>
              )}
            </div>
          )}

          <div style={{ height: 80 }}/>
        </div>

        {/* sticky CTA bar */}
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0,
          padding: '12px 22px calc(env(safe-area-inset-bottom, 0px) + 22px)',
          background: 'linear-gradient(to top, var(--paper) 75%, transparent)',
          display: 'flex', gap: 8,
        }}>
          <button className="btn ghost" style={{ flex: 1 }} onClick={() => onShare(activity.id)}>
            <span style={{ width: 18, height: 18 }}>{Icon.share}</span> Share
          </button>
          <button className="btn coral" style={{ flex: 2 }} onClick={() => onMarkDone(activity.id)}>
            <span style={{ width: 18, height: 18 }}>{Icon.check}</span> I went, rate it
          </button>
        </div>
      </div>
    </>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="col" style={{ gap: 1, flex: 1, minWidth: 0 }}>
      <span className="upper" style={{ fontSize: 9.5, color: 'var(--ink-mute)', fontWeight: 600 }}>{label}</span>
      <span style={{ fontWeight: 600, fontSize: 16, whiteSpace: 'nowrap' }} className="serif">{value}</span>
      <span style={{ fontSize: 10.5, color: 'var(--ink-mute)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</span>
    </div>
  );
}

// ── Share sheet ────────────────────────────────────────────────────────────
// Real friends, real sends. Three modes:
//   quick — POST /share to selected friends, optional note
//   plan  — create a real plan with this place attached (finalPlaceId)
//   link  — copy / Web-Share the real deep link
export function ShareSheet({ activity, friends, initialMode = 'quick', onClose, onShareToFriend, onMakePlan, onToast }) {
  const base = useHomeBase();
  const sheetRef = useSheetA11y(onClose);
  const [mode, setMode] = React.useState(initialMode); // quick | plan | link
  const [picked, setPicked] = React.useState([]);
  const [note, setNote] = React.useState('');
  const [planWhen, setPlanWhen] = React.useState(null); // epoch seconds | null
  const [planTitle, setPlanTitle] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const togglePicked = (id) => {
    setPicked(picked.includes(id) ? picked.filter(p => p !== id) : [...picked, id]);
  };
  if (!activity) return null;

  const link = shareUrlFor(activity.id);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      onToast('Link copied');
    } catch {
      onToast('Could not copy link');
    }
  };

  const webShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: activity.name, text: `Check out ${activity.name}`, url: link }); } catch {}
    } else {
      copyLink();
    }
  };

  const submit = () => {
    if (picked.length === 0 || sending) return;
    setSending(true);
    const done = () => setSending(false);
    if (mode === 'quick') {
      Promise.resolve(onShareToFriend(activity.id, picked, note.trim() || undefined)).finally(done);
    } else {
      Promise.resolve(onMakePlan(activity.id, picked, planWhen, planTitle.trim() || `${activity.name}?`)).finally(done);
    }
  };

  return (
    <>
      <div className="scrim" onClick={onClose}/>
      <div className="sheet" ref={sheetRef} tabIndex={-1} role="dialog" aria-label="Share">
        <div className="handle"/>
        <div className="row between" style={{ marginBottom: 10 }}>
          <h3 className="serif" style={{ fontSize: 26, margin: 0, color: 'var(--ink)' }}>Share <span style={{ color: 'var(--terracotta)' }}>this</span></h3>
          <button className="iconbtn" onClick={onClose} aria-label="Close">
            <span style={{ width: 16, height: 16 }}>{Icon.x}</span>
          </button>
        </div>

        {/* preview card */}
        <div style={{
          display: 'flex', gap: 12, padding: 10, borderRadius: 16,
          background: 'var(--bg-tint)', marginBottom: 16,
          border: '0.5px solid var(--hair)',
        }}>
          <div style={{ width: 70, flexShrink: 0 }}>
            <HeroArt activity={activity} height={70} rounded={10} showNav={false}/>
          </div>
          <div className="col" style={{ justifyContent: 'center', minWidth: 0 }}>
            <span className="serif" style={{ fontSize: 17, color: 'var(--ink)', lineHeight: 1.1, textWrap: 'pretty' }}>{activity.name}</span>
            <span className="muted" style={{ fontSize: 11.5 }}>{activity.kind} · {distLabel(activity, base)}</span>
          </div>
        </div>

        {/* mode tabs */}
        <div style={{ display: 'inline-flex', background: 'rgba(42,26,26,0.06)', borderRadius: 999, padding: 3, marginBottom: 14, alignSelf: 'flex-start' }}>
          {[
            { id: 'quick', label: 'To a friend' },
            { id: 'plan', label: 'Make a plan' },
            { id: 'link', label: 'Get a link' },
          ].map(t => (
            <button key={t.id} onClick={() => setMode(t.id)} style={{
              border: 0, background: mode === t.id ? 'var(--paper)' : 'transparent',
              borderRadius: 999, padding: '7px 12px', fontSize: 12, fontWeight: 500,
              color: mode === t.id ? 'var(--ink)' : 'var(--ink-mute)',
              boxShadow: mode === t.id ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
              cursor: 'pointer', fontFamily: 'inherit',
            }}>{t.label}</button>
          ))}
        </div>

        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, marginBottom: 14 }}>
          {mode === 'quick' && (
            friends.length === 0 ? (
              <div style={{ padding: '24px 8px', textAlign: 'center' }}>
                <p className="soft" style={{ fontSize: 14, margin: 0 }}>
                  No friends yet. Add some on the Friends tab, or send a link instead.
                </p>
              </div>
            ) : (
              <>
                <div className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600, marginBottom: 8 }}>
                  Send it to
                </div>
                {friends.map(f => (
                  <button key={f.userId} onClick={() => togglePicked(f.userId)} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    width: '100%', border: 0, background: 'transparent',
                    padding: '8px 0', cursor: 'pointer', fontFamily: 'inherit',
                  }}>
                    <FriendAvatar friend={f} size={36}/>
                    <div className="col" style={{ flex: 1, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: 14, fontWeight: 500 }}>{f.name}</span>
                      {f.handle && <span className="muted" style={{ fontSize: 11.5 }}>@{f.handle}</span>}
                    </div>
                    <div style={{
                      width: 22, height: 22, borderRadius: 999,
                      border: picked.includes(f.userId) ? '6px solid var(--coral)' : '1.5px solid var(--hair)',
                      background: picked.includes(f.userId) ? 'var(--coral)' : 'transparent',
                    }}/>
                  </button>
                ))}
                <div style={{ marginTop: 10 }}>
                  <input value={note} onChange={e => setNote(e.target.value.slice(0, 500))}
                         placeholder="Add a note (optional)"
                         aria-label="Note"
                         style={{
                           width: '100%', height: 38, boxSizing: 'border-box',
                           border: '0.5px solid var(--hair)', borderRadius: 10,
                           padding: '0 12px', fontFamily: 'inherit', fontSize: 13.5,
                           background: 'var(--bg-tint)', outline: 'none', color: 'var(--ink)',
                         }}/>
                </div>
              </>
            )
          )}
          {mode === 'plan' && (
            <>
              <div className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600, marginBottom: 8 }}>Name it</div>
              <input value={planTitle} onChange={e => setPlanTitle(e.target.value.slice(0, 120))}
                     placeholder={`${activity.name}?`}
                     aria-label="Plan title"
                     style={{
                       width: '100%', height: 38, boxSizing: 'border-box',
                       border: '0.5px solid var(--hair)', borderRadius: 10,
                       padding: '0 12px', fontFamily: 'inherit', fontSize: 13.5,
                       background: 'var(--bg-tint)', outline: 'none', color: 'var(--ink)',
                       marginBottom: 14,
                     }}/>
              <PlanForm friends={friends}
                        picked={picked} togglePicked={togglePicked}
                        setPlanWhen={setPlanWhen}/>
            </>
          )}
          {mode === 'link' && (
            <div className="col" style={{ gap: 14 }}>
              <p className="soft" style={{ margin: 0, fontSize: 14, lineHeight: 1.45 }}>
                A shareable card anyone can open: they'll see the place and a button to save it to their own list.
              </p>
              <div style={{
                padding: '10px 14px', borderRadius: 12,
                background: 'var(--bg-tint)', border: '0.5px solid var(--hair)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              }}>
                <span className="mono" style={{ fontSize: 12, color: 'var(--ink-soft)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {link.replace(/^https?:\/\//, '')}
                </span>
                <button className="chip coral" style={{ cursor: 'pointer', height: 28, padding: '0 12px', flexShrink: 0 }}
                        onClick={copyLink}>
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="row" style={{ gap: 8 }}>
                <button className="btn ghost" style={{ flex: 1, height: 44 }} onClick={webShare}>
                  <span style={{ width: 18, height: 18 }}>{Icon.share}</span> Share…
                </button>
              </div>
            </div>
          )}
        </div>

        {mode !== 'link' && (
          <button className="btn coral full" disabled={picked.length === 0 || sending}
                  style={{ opacity: picked.length === 0 ? 0.5 : 1 }}
                  onClick={submit}>
            {sending ? 'Sending…' : mode === 'quick'
              ? `Send to ${picked.length || 0} ${picked.length === 1 ? 'friend' : 'friends'}`
              : `Create plan · invite ${picked.length || 0}`}
          </button>
        )}
      </div>
    </>
  );
}

function PlanForm({ friends, picked, togglePicked, setPlanWhen }) {
  const { DATES, todayISO } = React.useMemo(() => {
    const today = new Date();
    const iso = today.toISOString().slice(0, 10);
    const dates = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
      const monthDay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return {
        key: d.toDateString(),
        date: d,
        chip: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : `${weekday} ${d.getDate()}`,
        label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : `${weekday}, ${monthDay}`,
      };
    });
    return { DATES: dates, todayISO: iso };
  }, []);

  const [selDate, setSelDate] = React.useState(DATES[0]);
  const [selTime, setSelTime] = React.useState({ h: 19, m: 0 });
  const [customDate, setCustomDate] = React.useState(null);
  const dateInputRef = React.useRef(null);
  const timeInputRef = React.useRef(null);

  const handleCustomDate = (e) => {
    const val = e.target.value;
    if (!val) return;
    const d = new Date(`${val}T12:00:00`);
    const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
    const monthDay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const cd = {
      key: d.toDateString(),
      date: d,
      chip: `${weekday} ${d.getDate()}`,
      label: `${weekday}, ${monthDay}`,
    };
    setCustomDate(cd);
    setSelDate(cd);
  };

  // Combine chosen day + time into epoch seconds for the API
  React.useEffect(() => {
    const d = new Date(selDate.date);
    d.setHours(selTime.h, selTime.m, 0, 0);
    setPlanWhen(Math.floor(d.getTime() / 1000));
  }, [selDate.key, selTime.h, selTime.m]);

  const timeLabel = (() => {
    const suffix = selTime.h >= 12 ? 'pm' : 'am';
    const hour = selTime.h % 12 || 12;
    return selTime.m === 0 ? `${hour}${suffix}` : `${hour}:${String(selTime.m).padStart(2, '0')}${suffix}`;
  })();

  const chipBtn = (active) => ({
    flexShrink: 0, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
    border: active ? 0 : '0.5px solid var(--hair)',
    background: active ? 'var(--ink)' : 'var(--paper)',
    color: active ? 'var(--paper)' : 'var(--ink)',
    borderRadius: 999,
  });

  return (
    <>
      <div className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600, marginBottom: 8 }}>Day</div>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2, scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', marginBottom: 14 }}>
        {DATES.map(d => (
          <button key={d.key} onClick={() => setSelDate(d)}
                  style={{ ...chipBtn(selDate.key === d.key), height: 32, padding: '0 14px', fontSize: 13 }}>
            {d.chip}
          </button>
        ))}
        {/* custom date picker */}
        <button
          onClick={() => { try { dateInputRef.current?.showPicker(); } catch { dateInputRef.current?.click(); } }}
          style={{ ...chipBtn(!!customDate && selDate.key === customDate.key), height: 32, padding: '0 11px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 5 }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
            <rect x="3" y="4" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8"/>
            <path d="M3 9h18" stroke="currentColor" strokeWidth="1.8"/>
            <path d="M8 2v4M16 2v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          {customDate && selDate.key === customDate.key ? customDate.chip : 'More'}
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
            <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <input ref={dateInputRef} type="date" min={todayISO} onChange={handleCustomDate}
               tabIndex={-1} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}/>
      </div>

      <div className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600, marginBottom: 8 }}>Time</div>
      <div style={{ marginBottom: 16, position: 'relative' }}>
        <button
          onClick={() => { try { timeInputRef.current?.showPicker(); } catch { timeInputRef.current?.click(); } }}
          style={{ ...chipBtn(true), height: 36, padding: '0 14px 0 12px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 7, borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
        >
          <span style={{ width: 14, height: 14, flexShrink: 0, opacity: 0.7 }}>{Icon.clock}</span>
          {timeLabel}
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, opacity: 0.6 }}>
            <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <input ref={timeInputRef} type="time" defaultValue="19:00"
               onChange={e => {
                 const [h, m] = e.target.value.split(':').map(Number);
                 if (!Number.isNaN(h)) setSelTime({ h, m: m || 0 });
               }}
               tabIndex={-1} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}/>
      </div>

      <div className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600, marginBottom: 8 }}>
        Invite ({picked.length})
      </div>
      {friends.length === 0 ? (
        <p className="soft" style={{ fontSize: 13, margin: 0 }}>No friends yet. Add some on the Friends tab first.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          {friends.map(f => {
            const on = picked.includes(f.userId);
            const displayName = f.name || f.handle || f.userId.slice(0, 8);
            return (
              <button key={f.userId} onClick={() => togglePicked(f.userId)} style={{
                border: 0, background: 'transparent', padding: 6,
                borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                // inset so the ring can't be clipped by the sheet's scroll container
                boxShadow: on ? 'inset 0 0 0 2px var(--coral)' : 'none',
                transition: 'box-shadow 0.12s',
              }}>
                <FriendAvatar friend={f} size={44}/>
                <span style={{ fontSize: 10.5, fontWeight: 500, textAlign: 'center' }}>{displayName.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}

// ── Rate sheet ─────────────────────────────────────────────────────────────
export function RateSheet({ activity, existing, onClose, onSave }) {
  const sheetRef = useSheetA11y(onClose);
  const [stars, setStars] = React.useState(existing?.stars || 0);
  const [axes, setAxes] = React.useState(existing?.axes || { vibe: 0, value: 0, again: 0 });
  const [note, setNote] = React.useState(existing?.note || '');
  if (!activity) return null;
  const canSave = stars > 0;
  return (
    <>
      <div className="scrim" onClick={onClose}/>
      <div className="sheet" ref={sheetRef} tabIndex={-1} role="dialog" aria-label="Rate this place">
        <div className="handle"/>
        <div className="row between" style={{ marginBottom: 12 }}>
          <h3 className="serif" style={{ fontSize: 26, margin: 0, color: 'var(--ink)' }}>
            How was <span style={{ color: 'var(--terracotta)' }}>it</span>?
          </h3>
          <button className="iconbtn" onClick={onClose} aria-label="Close">
            <span style={{ width: 16, height: 16 }}>{Icon.x}</span>
          </button>
        </div>

        <div style={{
          display: 'flex', gap: 12, padding: 10, borderRadius: 16,
          background: 'var(--bg-tint)', marginBottom: 16,
          border: '0.5px solid var(--hair)',
        }}>
          <div style={{ width: 60, flexShrink: 0 }}>
            <HeroArt activity={activity} height={60} rounded={10} showNav={false}/>
          </div>
          <div className="col" style={{ justifyContent: 'center', minWidth: 0 }}>
            <span className="serif" style={{ fontSize: 17, color: 'var(--ink)', lineHeight: 1.1, textWrap: 'pretty' }}>{activity.name}</span>
            <span className="muted" style={{ fontSize: 11.5 }}>{activity.kind}{activity.address ? ` · ${activity.address}` : ''}</span>
          </div>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {/* overall stars */}
          <div className="col" style={{ alignItems: 'center', gap: 8, marginBottom: 18 }}>
            <span className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600 }}>Overall</span>
            <Stars value={stars} onChange={setStars} size={32}/>
          </div>

          {/* multi-axis */}
          <div className="col" style={{ gap: 14, marginBottom: 18 }}>
            <AxisSlider label="Vibe" hint="atmosphere, energy, crowd" value={axes.vibe}
                        onChange={v => setAxes({ ...axes, vibe: v })}/>
            <AxisSlider label="Value" hint="worth what you paid" value={axes.value}
                        onChange={v => setAxes({ ...axes, value: v })}/>
            <AxisSlider label="Would do it again" hint="all things considered" value={axes.again}
                        onChange={v => setAxes({ ...axes, again: v })}/>
          </div>

          {/* note */}
          <div className="col" style={{ gap: 6, marginBottom: 16 }}>
            <span className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600 }}>One-line memory (optional)</span>
            <textarea value={note} onChange={e => setNote(e.target.value)}
                      placeholder="The thing you'll want to remember…"
                      aria-label="Note"
                      style={{
                        width: '100%', resize: 'none', minHeight: 64,
                        background: 'var(--bg-tint)', border: '0.5px solid var(--hair)',
                        borderRadius: 12, padding: '10px 12px', fontFamily: 'inherit',
                        fontSize: 14, color: 'var(--ink)', outline: 'none', lineHeight: 1.4,
                      }} maxLength={140}/>
            <span className="muted" style={{ fontSize: 10.5, textAlign: 'right' }}>{note.length}/140</span>
          </div>
        </div>

        <button className="btn coral full" disabled={!canSave}
                style={{ opacity: canSave ? 1 : 0.5, marginTop: 8 }}
                onClick={() => onSave(activity.id, { stars, axes, note })}>
          Stamp it
        </button>
      </div>
    </>
  );
}

function AxisSlider({ label, hint, value, onChange }) {
  return (
    <div className="col" style={{ gap: 4 }}>
      <div className="row between">
        <div className="row" style={{ gap: 6, alignItems: 'baseline' }}>
          <span style={{ fontSize: 13.5, fontWeight: 500 }}>{label}</span>
          <span className="muted" style={{ fontSize: 11 }}>{hint}</span>
        </div>
        <Stars value={value} onChange={onChange} size={16}/>
      </div>
    </div>
  );
}

// ── Location picker ────────────────────────────────────────────────────────
export function LocationPicker({ locationId, onClose, onPick, onPickGPS }) {
  const sheetRef = useSheetA11y(onClose);
  const [q, setQ] = React.useState('');
  const [locating, setLocating] = React.useState(false);
  const [locError, setLocError] = React.useState(false);
  const list = LOCATIONS.filter(l => l.name.toLowerCase().includes(q.trim().toLowerCase()));

  const useCurrentLocation = () => {
    if (!navigator.geolocation) { setLocError(true); return; }
    setLocating(true);
    setLocError(false);
    navigator.geolocation.getCurrentPosition(
      pos => { setLocating(false); onPickGPS(pos.coords.latitude, pos.coords.longitude); },
      () => { setLocating(false); setLocError(true); },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  };

  return (
    <>
      <div className="scrim" onClick={onClose}/>
      <div className="sheet" ref={sheetRef} tabIndex={-1} role="dialog" aria-label="Choose location">
        <div className="handle"/>
        <div className="row between" style={{ marginBottom: 14 }}>
          <h3 className="serif" style={{ fontSize: 26, margin: 0, color: 'var(--ink)' }}>
            Where <span style={{ color: 'var(--terracotta)' }}>to</span>?
          </h3>
          <button className="iconbtn" onClick={onClose} aria-label="Close">
            <span style={{ width: 16, height: 16 }}>{Icon.x}</span>
          </button>
        </div>

        {/* search */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'var(--bg-tint)', border: '0.5px solid var(--hair)',
          borderRadius: 12, padding: '0 12px', height: 46, marginBottom: 12,
        }}>
          <span style={{ width: 18, height: 18, color: 'var(--ink-mute)' }}>{Icon.pin}</span>
          <input value={q} onChange={e => setQ(e.target.value)}
                 aria-label="Search neighborhoods"
                 style={{
                   flex: 1, border: 0, background: 'transparent', outline: 'none',
                   fontFamily: 'inherit', fontSize: 15, color: 'var(--ink)',
                 }}/>
          {q && (
            <button className="iconbtn" style={{ width: 26, height: 26 }} onClick={() => setQ('')} aria-label="Clear">
              <span style={{ width: 12, height: 12 }}>{Icon.x}</span>
            </button>
          )}
        </div>

        {/* current location — real geolocation */}
        <button onClick={useCurrentLocation} disabled={locating} style={{
          display: 'flex', alignItems: 'center', gap: 12, width: '100%',
          border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit',
          padding: '10px 4px', marginBottom: 4,
        }}>
          <span style={{
            width: 38, height: 38, borderRadius: 999, flexShrink: 0,
            background: 'rgba(229,97,58,0.15)', color: 'var(--coral)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ width: 18, height: 18 }}>{Icon.compass}</span>
          </span>
          <div className="col" style={{ alignItems: 'flex-start' }}>
            <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--coral)' }}>
              {locating ? 'Locating…' : 'Use current location'}
            </span>
            <span className="muted" style={{ fontSize: 11.5 }}>
              {locError ? "Couldn't get your location. Check permissions" : 'Uses your device GPS'}
            </span>
          </div>
        </button>

        <div className="divider"/>

        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, marginBottom: 4 }}>
          {list.length === 0 && (
            <div className="muted" style={{ fontSize: 13, padding: '18px 4px', textAlign: 'center' }}>
              No neighborhood by that name yet.
            </div>
          )}
          {list.map(l => {
            const active = l.id === locationId;
            return (
              <button key={l.id} onClick={() => onPick(l.id)} style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit',
                padding: '11px 4px', borderBottom: '0.5px solid var(--hair)',
              }}>
                <span style={{
                  width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                  background: active ? 'var(--coral)' : 'rgba(42,26,26,0.06)',
                  color: active ? 'var(--paper)' : 'var(--ink)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ width: 16, height: 16 }}>{Icon.pin}</span>
                </span>
                <div className="col" style={{ alignItems: 'flex-start', flex: 1 }}>
                  <span style={{ fontSize: 14.5, fontWeight: 500 }}>{l.name}</span>
                </div>
                {active && (
                  <span style={{ width: 20, height: 20, color: 'var(--coral)' }}>{Icon.check}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ── Plan detail ────────────────────────────────────────────────────────────
// Full plan lifecycle. Host: finalize a place (from saved list), invite more
// friends, remove invitees, edit time, cancel. Invitee: RSVP, leave.
export function PlanDetailSheet({ plan, friends, savedActivities, activities, onClose, onOpenActivity, onPlanChange, onPlanGone, onToast }) {
  const sheetRef = useSheetA11y(onClose);
  const uid = larkleGetUid();
  const isHost = plan.organizerId === uid;
  const [busy, setBusy] = React.useState(false);
  const [pickingPlace, setPickingPlace] = React.useState(false);
  const [inviting, setInviting] = React.useState(false);
  const [editingTime, setEditingTime] = React.useState(false);
  const [confirmingCancel, setConfirmingCancel] = React.useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = React.useState(null); // invitee pending removal confirm
  const [newTime, setNewTime] = React.useState('');
  const [invitePick, setInvitePick] = React.useState([]);

  // Resolve the finalized place (deck → saved → direct fetch)
  const [fetchedPlace, setFetchedPlace] = React.useState(null);
  const localPlace = plan.finalPlaceId
    ? (activities.find(a => a.id === plan.finalPlaceId) || savedActivities.find(a => a.id === plan.finalPlaceId))
    : null;
  React.useEffect(() => {
    if (!plan.finalPlaceId || localPlace) { setFetchedPlace(null); return; }
    let alive = true;
    apiFetchLocation(plan.finalPlaceId)
      .then(p => { if (alive && p) setFetchedPlace(placeToActivity(p)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [plan.finalPlaceId, !!localPlace]);
  const place = localPlace || fetchedPlace;

  const run = (promise, successMsg, { closes = false, deletes = false } = {}) => {
    if (busy) return;
    setBusy(true);
    promise
      .then(updated => {
        if (deletes) onPlanGone(plan.id);
        else if (updated) onPlanChange(updated);
        if (successMsg) onToast(successMsg);
        if (closes) onClose();
      })
      .catch(() => onToast('Something went wrong'))
      .finally(() => setBusy(false));
  };

  const myInvitee = plan.invitees?.find(inv => inv.userId === uid);
  const myRsvp = myInvitee?.rsvp;
  const invitedIds = new Set((plan.invitees || []).map(i => i.userId));
  const invitableFriends = friends.filter(f => !invitedIds.has(f.userId));

  const scheduledStr = plan.scheduledFor
    ? new Date(plan.scheduledFor * 1000).toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    : 'No time set';

  const friendFor = (id) => friends.find(f => f.userId === id);
  const rsvpColor = { Yes: 'var(--green)', Maybe: 'var(--gold)', No: 'var(--red)', Pending: 'var(--ink-mute)' };
  const statusChip = { Open: '#E8A33C', Finalized: '#5c7a4a', Cancelled: '#c44', Completed: '#888' }[plan.status] || '#888';

  return (
    <>
      <div className="scrim" onClick={onClose}/>
      <div className="sheet" ref={sheetRef} tabIndex={-1} role="dialog" aria-label={plan.title} style={{ maxHeight: '92%' }}>
        <div className="handle"/>
        <div className="row between" style={{ marginBottom: 4 }}>
          <h3 className="serif" style={{ fontSize: 26, margin: 0, color: 'var(--ink)', flex: 1, marginRight: 10 }}>{plan.title}</h3>
          <button className="iconbtn" onClick={onClose} aria-label="Close">
            <span style={{ width: 16, height: 16 }}>{Icon.x}</span>
          </button>
        </div>
        <div className="row" style={{ gap: 8, marginBottom: 12 }}>
          {plan.status !== 'Finalized' && (
            <span style={{
              fontSize: 10.5, fontWeight: 600, padding: '3px 9px', borderRadius: 999,
              background: statusChip + '28', color: statusChip, letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>{plan.status}</span>
          )}
          {isHost && <span className="chip gold" style={{ fontSize: 10.5 }}>You're hosting</span>}
        </div>

        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {/* When */}
          <div className="row between" style={{ marginBottom: 12 }}>
            <div className="row" style={{ gap: 8, fontSize: 13.5, color: 'var(--ink)' }}>
              <span style={{ width: 15, height: 15, color: 'var(--ink-mute)' }}>{Icon.cal}</span>
              {scheduledStr}
            </div>
            {isHost && plan.status !== 'Cancelled' && (
              <button className="chip" style={{ cursor: 'pointer', height: 26, padding: '0 11px', fontSize: 11.5 }}
                      onClick={() => setEditingTime(t => !t)}>
                {editingTime ? 'Never mind' : 'Change'}
              </button>
            )}
          </div>
          {editingTime && (
            <div className="row" style={{ gap: 8, marginBottom: 14 }}>
              <input type="datetime-local" value={newTime} onChange={e => setNewTime(e.target.value)}
                     aria-label="New time"
                     style={{
                       flex: 1, height: 36, border: '0.5px solid var(--hair)', borderRadius: 10,
                       padding: '0 12px', fontFamily: 'inherit', fontSize: 13, background: 'var(--bg-tint)',
                       outline: 'none', color: 'var(--ink)', boxSizing: 'border-box',
                     }}/>
              <button className="btn coral" style={{ height: 36, padding: '0 14px', fontSize: 12.5 }}
                      disabled={!newTime || busy}
                      onClick={() => {
                        run(apiUpdatePlan(plan.id, { scheduledFor: Math.floor(new Date(newTime).getTime() / 1000) }), 'Time updated');
                        setEditingTime(false);
                      }}>
                Save
              </button>
            </div>
          )}
          {plan.scheduledFor && plan.status !== 'Cancelled' && (
            <div className="row" style={{ gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
              <span className="soft" style={{ fontSize: 11.5, marginRight: 2 }}>Add to calendar</span>
              <a href={googleCalendarUrl(plan, place)} target="_blank" rel="noopener noreferrer"
                 className="chip" style={{ height: 26, padding: '0 11px', fontSize: 11.5, textDecoration: 'none' }}>
                Google
              </a>
              <a href={outlookCalendarUrl(plan, place)} target="_blank" rel="noopener noreferrer"
                 className="chip" style={{ height: 26, padding: '0 11px', fontSize: 11.5, textDecoration: 'none' }}>
                Outlook
              </a>
              <button className="chip" style={{ height: 26, padding: '0 11px', fontSize: 11.5, cursor: 'pointer' }}
                      onClick={() => downloadIcs(plan, place)}>
                Apple / .ics
              </button>
            </div>
          )}

          {/* Place */}
          {place ? (
            <div onClick={() => onOpenActivity(place.id)} style={{
              display: 'flex', gap: 10, marginBottom: 14, cursor: 'pointer',
              background: 'var(--bg-tint)', borderRadius: 12, overflow: 'hidden',
            }}>
              <div style={{ width: 84, flexShrink: 0 }}>
                <HeroArt activity={place} height={84} rounded={0} showNav={false}/>
              </div>
              <div className="col" style={{ justifyContent: 'center', padding: '0 8px 0 0', gap: 3, minWidth: 0 }}>
                <span className="upper" style={{ fontSize: 9, color: 'var(--terracotta)', fontWeight: 700 }}>The spot</span>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{place.name}</span>
                <span style={{ fontSize: 11.5, color: 'var(--ink-mute)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{place.address || place.kind}</span>
              </div>
            </div>
          ) : plan.status === 'Open' && (
            <div style={{
              padding: '12px 14px', borderRadius: 12, marginBottom: 14,
              background: 'var(--bg-tint)', border: '0.5px dashed var(--hair)',
            }}>
              <div className="row between">
                <span className="soft" style={{ fontSize: 13 }}>No place locked in yet</span>
                {isHost && (
                  <button className="chip coral" style={{ cursor: 'pointer', height: 28, padding: '0 12px' }}
                          onClick={() => setPickingPlace(p => !p)}>
                    {pickingPlace ? 'Never mind' : 'Pick a place'}
                  </button>
                )}
              </div>
              {pickingPlace && (
                <div style={{ marginTop: 10, maxHeight: 220, overflowY: 'auto' }}>
                  {savedActivities.length === 0 ? (
                    <p className="soft" style={{ fontSize: 12.5, margin: '6px 0 0' }}>
                      Your saved list is empty. Save some places on Discover first.
                    </p>
                  ) : savedActivities.map(a => (
                    <button key={a.id} disabled={busy}
                            onClick={() => run(apiFinalizePlan(plan.id, a.id), `Locked in ${a.name}`)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                              border: 0, background: 'transparent', cursor: 'pointer',
                              fontFamily: 'inherit', padding: '7px 2px', textAlign: 'left',
                              borderBottom: '0.5px solid var(--hair)',
                            }}>
                      <div style={{ width: 40, height: 40, borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                        <HeroArt activity={a} height={40} rounded={0} showNav={false}/>
                      </div>
                      <div className="col" style={{ minWidth: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                        <span style={{ fontSize: 11, color: 'var(--ink-mute)' }}>{a.kind}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* People */}
          <div className="row between" style={{ marginBottom: 8 }}>
            <span className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600 }}>
              Who's in
            </span>
            {isHost && plan.status !== 'Cancelled' && invitableFriends.length > 0 && (
              <button className="chip" style={{ cursor: 'pointer', height: 26, padding: '0 11px', fontSize: 11.5 }}
                      onClick={() => { setInviting(i => !i); setInvitePick([]); }}>
                {inviting ? 'Never mind' : '+ Invite more'}
              </button>
            )}
          </div>

          {inviting && (
            <div style={{ marginBottom: 12, padding: '10px 12px', background: 'var(--bg-tint)', borderRadius: 12 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 10 }}>
                {invitableFriends.map(f => {
                  const sel = invitePick.includes(f.userId);
                  return (
                    <button key={f.userId}
                            onClick={() => setInvitePick(s => sel ? s.filter(id => id !== f.userId) : [...s, f.userId])}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px',
                              borderRadius: 999, border: 0, cursor: 'pointer', fontFamily: 'inherit',
                              fontSize: 12.5, fontWeight: 500,
                              background: sel ? 'var(--ink)' : 'var(--paper)',
                              color: sel ? 'var(--paper)' : 'var(--ink)',
                            }}>
                      <FriendAvatar friend={f} size={20}/>
                      {(f.name || f.handle || '?').split(' ')[0]}
                    </button>
                  );
                })}
              </div>
              <button className="btn coral full" style={{ height: 34, fontSize: 12.5 }}
                      disabled={invitePick.length === 0 || busy}
                      onClick={() => { run(apiInviteToPlan(plan.id, invitePick), 'Invites sent'); setInviting(false); }}>
                Invite {invitePick.length || ''}
              </button>
            </div>
          )}

          {(plan.invitees || []).map(inv => {
            const f = friendFor(inv.userId);
            const isMe = inv.userId === uid;
            const label = isMe ? 'You' : (f?.name || f?.handle || inv.userId.slice(0, 8));
            const confirming = confirmRemoveId === inv.userId;
            return (
              <div key={inv.userId} className="row between" style={{
                padding: confirming ? '7px 8px' : '7px 0',
                margin: confirming ? '0 -8px' : 0,
                borderBottom: '0.5px solid var(--hair)',
                background: confirming ? 'rgba(196,68,68,0.07)' : 'transparent',
                borderRadius: confirming ? 10 : 0,
                transition: 'background 0.18s',
              }}>
                <div className="row" style={{ gap: 10, minWidth: 0 }}>
                  {f ? <FriendAvatar friend={f} size={32}/> : <GenerativeAvatar userId={inv.userId} size={32}/>}
                  <div className="col" style={{ gap: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 500 }}>
                      {confirming ? `Remove ${label.split(' ')[0]} from the plan?` : label}
                      {!confirming && inv.role === 'organizer' && <span className="muted" style={{ fontSize: 11 }}> · host</span>}
                    </span>
                    {confirming && <span style={{ fontSize: 11, color: 'var(--ink-mute)' }}>They won't be notified</span>}
                  </div>
                </div>
                {confirming ? (
                  <div className="row" style={{ gap: 6 }}>
                    <button disabled={busy}
                            onClick={() => { run(apiRemoveFromPlan(plan.id, inv.userId), 'Removed'); setConfirmRemoveId(null); }}
                            style={{
                              height: 28, padding: '0 12px', border: 0,
                              borderRadius: 8, background: '#c44', color: '#fff',
                              fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            }}>Remove</button>
                    <button onClick={() => setConfirmRemoveId(null)} style={{
                      height: 28, padding: '0 12px', border: '0.5px solid var(--hair)',
                      borderRadius: 8, background: 'var(--paper)', color: 'var(--ink)',
                      fontFamily: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    }}>Keep</button>
                  </div>
                ) : (
                  <div className="row" style={{ gap: 8 }}>
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: rsvpColor[inv.rsvp] || 'var(--ink-mute)' }}>
                      {inv.rsvp === 'Pending' ? 'no reply' : inv.rsvp}
                    </span>
                    {isHost && !isMe && plan.status !== 'Cancelled' && (
                      <button className="iconbtn" style={{ width: 26, height: 26 }} aria-label={`Remove ${label}`}
                              disabled={busy}
                              onClick={() => setConfirmRemoveId(inv.userId)}>
                        <span style={{ width: 10, height: 10 }}>{Icon.x}</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* My RSVP */}
          {/* Finalized = place locked, attendance still open — only Cancelled/Completed close RSVPs */}
          {myRsvp !== undefined && (plan.status === 'Open' || plan.status === 'Finalized') && (
            <div style={{ marginTop: 14 }}>
              <div className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600, marginBottom: 8 }}>Your RSVP</div>
              <div className="row" style={{ gap: 8 }}>
                {[['Yes', "I'm in"], ['Maybe', 'Maybe'], ['No', "Can't"]].map(([val, label]) => (
                  <button key={val} disabled={busy}
                          onClick={() => run(apiRsvpToPlan(plan.id, val))}
                          style={{
                            flex: 1, height: 36, border: 0, borderRadius: 10, cursor: 'pointer',
                            fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
                            background: myRsvp === val ? 'var(--ink)' : 'rgba(42,26,26,0.07)',
                            color: myRsvp === val ? 'var(--paper)' : 'var(--ink)',
                          }}>{label}</button>
                ))}
              </div>
            </div>
          )}

          <div style={{ height: 12 }}/>
        </div>

        {/* Destructive actions */}
        {plan.status !== 'Cancelled' && (
          isHost ? (
            confirmingCancel ? (
              <div className="row" style={{ gap: 8, marginTop: 8 }}>
                <button className="btn full" disabled={busy} style={{ flex: 1, height: 42, background: 'var(--red)', fontSize: 13.5 }}
                        onClick={() => run(apiCancelPlan(plan.id), 'Plan cancelled', { closes: true, deletes: true })}>
                  Yes, cancel the plan
                </button>
                <button className="btn ghost" style={{ flex: 1, height: 42, fontSize: 13.5 }} onClick={() => setConfirmingCancel(false)}>
                  Keep it
                </button>
              </div>
            ) : (
              <button disabled={busy} onClick={() => setConfirmingCancel(true)} style={{
                marginTop: 8, width: '100%', height: 40, border: 0, borderRadius: 12, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
                background: 'transparent', color: 'var(--red)',
              }}>
                Cancel this plan
              </button>
            )
          ) : (
            <button disabled={busy} style={{
              marginTop: 8, width: '100%', height: 40, border: 0, borderRadius: 12, cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
              background: 'transparent', color: 'var(--red)',
            }} onClick={() => run(apiLeavePlan(plan.id), 'You left the plan', { closes: true, deletes: true })}>
              Leave this plan
            </button>
          )
        )}
      </div>
    </>
  );
}
